import { useState, useEffect } from 'react';
import { useLoyalty } from '../../../contexts/LoyaltyContext';
import { apiGet } from '../../../lib/api';

interface ReferralResponse {
  referralCode: string;
  referralCount: number;
}

interface LoyaltyInviteFriendsProps {
  title?: string;
  subtitle?: string;
  showLeagueRules?: boolean;
}

function LoginCta() {
  return (
    <div className="loyalty-card p-8 text-center">
      <div className="text-4xl mb-3 opacity-50">&#128140;</div>
      <h2 className="loyalty-heading text-xl mb-2 text-[var(--color-text)]">
        Invite your friends
      </h2>
      <p className="text-[var(--color-text-muted)] text-sm mb-4">
        Sign in to get your referral link and earn bonus points.
      </p>
      <a
        href="/login"
        className="inline-block px-6 py-2 rounded-lg bg-[var(--color-primary)] text-white font-semibold text-sm hover:opacity-90 transition-opacity"
      >
        Sign In
      </a>
    </div>
  );
}

export function LoyaltyInviteFriendsBlock({ props }: { props: LoyaltyInviteFriendsProps }) {
  const { data, loading: loyaltyLoading } = useLoyalty();
  const title = props.title ?? 'Want to climb faster?';
  const subtitle = props.subtitle ?? 'Invite your friends to earn a 500pt referral bonus';
  const showLeagueRules = props.showLeagueRules ?? false;

  const [referral, setReferral] = useState<ReferralResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!data) return;
    let cancelled = false;

    apiGet<ReferralResponse>('/customer/loyalty/referral')
      .then((res) => {
        if (!cancelled) setReferral(res);
      })
      .catch(() => {
        // silently fail - referral is optional
      });

    return () => { cancelled = true; };
  }, [data]);

  const handleInvite = async () => {
    if (!referral) return;

    const referralLink = `${window.location.origin}?ref=${referral.referralCode}`;

    // Try native share first (mobile), fallback to clipboard
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on the loyalty program!',
          text: subtitle,
          url: referralLink,
        });
        return;
      } catch {
        // share cancelled or failed, fall through to clipboard
      }
    }

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not available
    }
  };

  if (loyaltyLoading) {
    return (
      <div className="loyalty-card p-6 animate-pulse">
        <div className="h-6 w-48 bg-[var(--color-surface-border)] rounded mb-2" />
        <div className="h-4 w-64 bg-[var(--color-surface-border)] rounded mb-4" />
        <div className="flex gap-3">
          <div className="h-10 w-32 bg-[var(--color-surface-border)] rounded-lg" />
          <div className="h-10 w-28 bg-[var(--color-surface-border)] rounded-lg" />
        </div>
      </div>
    );
  }

  if (!data) return <LoginCta />;

  return (
    <div className="loyalty-card p-6 sm:p-8">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {/* Text content */}
        <div className="flex-1">
          <h2 className="loyalty-heading text-lg sm:text-xl text-[var(--color-text)] mb-1">
            {title}
          </h2>
          <p className="text-sm text-[var(--color-text-muted)]">
            {subtitle}
          </p>
          {referral && referral.referralCount > 0 && (
            <p className="text-xs text-[var(--color-primary)] font-semibold mt-2">
              {referral.referralCount} friend{referral.referralCount !== 1 ? 's' : ''} already invited!
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex gap-3 flex-shrink-0">
          <button
            onClick={handleInvite}
            disabled={!referral}
            className="px-5 py-2.5 rounded-lg bg-[var(--color-primary)] text-white font-bold text-sm hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copied ? 'Link Copied!' : 'Invite Friends'}
          </button>
          {showLeagueRules ? (
            <a
              href="/league-rules"
              className="px-5 py-2.5 rounded-lg border-2 border-[var(--color-primary)] text-[var(--color-primary)] font-bold text-sm hover:bg-[var(--color-primary)]/10 transition-colors"
            >
              League Rules
            </a>
          ) : (
            <a
              href="/referral"
              className="px-5 py-2.5 rounded-lg border-2 border-[var(--color-primary)] text-[var(--color-primary)] font-bold text-sm hover:bg-[var(--color-primary)]/10 transition-colors"
            >
              Learn More
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
